import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, fmtItalianDate, fmtItalianTime, itParts, WEEKDAYS_IT } from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MapPin, Sparkles, Clock, Calendar as CalendarIcon, Check, Loader2 } from "lucide-react";

const REASONS = [
  "Prima visita",
  "Controllo",
  "Dolore cervicale",
  "Mal di schiena",
  "Infortunio sportivo",
  "Riabilitazione",
  "Altro",
];

function StepBar({ step }) {
  return (
    <div className="flex items-center gap-1.5 mb-8" data-testid="step-bar">
      {[1, 2, 3, 4].map((n) => (
        <div key={n} className={`h-1.5 flex-1 rounded-full transition-colors ${n <= step ? "bg-[#2D4A3E]" : "bg-[#E0E5E2]"}`} />
      ))}
    </div>
  );
}

function formatDay(iso) {
  return fmtItalianDate(iso);
}

export default function Booking({ embed = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState(null);
  const [studioPreSelected, setStudioPreSelected] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    note: "",
    consent_gdpr: false,
    consent_privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/studios").then((r) => setStudios(r.data)).catch(() => {});
  }, []);

  // Pre-select studio from ?studio=... query param (from Landing preview modal)
  useEffect(() => {
    const preStudio = searchParams.get("studio");
    if (preStudio && studios.some(s => s.id === preStudio) && !studioId) {
      setStudioId(preStudio);
      setStudioPreSelected(true);
    }
  }, [searchParams, studios, studioId]);

  useEffect(() => {
    if (!studioId) return;
    setLoadingSlots(true);
    const romeFmt = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" });
    const from_date = romeFmt.format(new Date());
    const t = new Date(); t.setDate(t.getDate() + 30);
    const to_date = romeFmt.format(t);
    api
      .get("/slots", { params: { studio_id: studioId, from_date, to_date } })
      .then((r) => setSlotsByDate(r.data))
      .catch(() => setSlotsByDate({}))
      .finally(() => setLoadingSlots(false));
  }, [studioId]);

  const sortedDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  const findFirstAvailable = async () => {
    if (!studioId) return;
    const r = await api.get("/first-available", { params: { studio_id: studioId } });
    if (r.data?.slot) {
      setSelectedSlot({ date: r.data.date, ...r.data.slot });
      toast.success(`Prima disponibilità: ${formatDay(r.data.date)} alle ${r.data.slot.label}`);
    } else {
      toast.error("Nessuna disponibilità trovata nei prossimi giorni");
    }
  };

  const canNext1 = !!reason;
  const canNext2 = !!studioId;
  const canNext3 = !!selectedSlot;
  const canSubmit =
    form.first_name.trim() && form.last_name.trim() && form.phone.trim() && /\S+@\S+\.\S+/.test(form.email.trim()) && form.consent_gdpr && form.consent_privacy;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        studio_id: studioId,
        start: selectedSlot.start,
        reason,
        ...form,
      };
      const { data } = await api.post("/appointments", payload);
      if (embed) {
        // Redirect the top-most window if inside iframe, so confirmation is shown fullscreen
        try {
          if (window.top && window.top !== window.self) {
            window.top.location.href = `${window.location.origin}/conferma/${data.id}`;
            return;
          }
        } catch (_) { /* cross-origin - fall through */ }
        navigate(`/conferma/${data.id}`);
      } else {
        navigate(`/conferma/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const currentStudio = studios.find((s) => s.id === studioId);

  return (
    <div className="min-h-screen">
      {!embed && <Header minimal />}
      <main className={`max-w-2xl mx-auto px-5 md:px-8 ${embed ? "pt-6" : "pt-8"} pb-24`}>
        <StepBar step={step} />

        {step === 1 && (
          <div className="fade-up">
            <div className="overline mb-2">Passaggio 1 di 4</div>
            <h1 className="font-display text-3xl text-[#1A231F]">Qual è il motivo della visita?</h1>
            <p className="text-[#5C7066] mt-2">Scegli quello che descrive meglio la tua esigenza.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {REASONS.map((r) => (
                <button
                  key={r}
                  data-testid={`reason-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => { setReason(r); setStep(studioPreSelected ? 3 : 2); }}
                  className={`text-left px-5 py-4 rounded-xl border transition-colors duration-200 ${
                    reason === r
                      ? "border-[#2D4A3E] bg-[#2D4A3E] text-white"
                      : "border-[#E0E5E2] bg-white text-[#1A231F] hover:border-[#8AA399]"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <Button
                data-testid="step1-next"
                disabled={!canNext1}
                onClick={() => setStep(studioPreSelected ? 3 : 2)}
                className="h-14 px-6 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26] text-white"
              >
                Continua <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="fade-up">
            <div className="overline mb-2">Passaggio 2 di 4</div>
            <h1 className="font-display text-3xl text-[#1A231F]">Scegli lo studio</h1>
            <p className="text-[#5C7066] mt-2">Entrambi gli studi sono a Milano.</p>
            <div className="mt-6 grid gap-5">
              {studios.map((s) => (
                <div
                  key={s.id}
                  data-testid={`studio-card-${s.id}`}
                  className={`card-soft p-6 cursor-pointer ${studioId === s.id ? "ring-2 ring-[#2D4A3E]" : ""}`}
                  onClick={() => { setStudioId(s.id); setStep(3); }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-xl bg-[#FAF9F6] grid place-items-center text-[#2D4A3E]">
                      <MapPin size={18} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-xl text-[#1A231F]">{s.name}</h3>
                      <div className="text-sm text-[#5C7066] mt-0.5">{s.address}</div>
                      <div className="mt-3 space-y-1">
                        {s.schedule.map((sc, i) => (
                          <div key={i} className="text-xs text-[#5C7066]">
                            {WEEKDAYS_IT[sc.weekday]}: {sc.start} → ultimo appuntamento {sc.last_start}
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 flex gap-3">
                        <a
                          data-testid={`studio-maps-${s.id}`}
                          href={s.maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#2D4A3E] underline underline-offset-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Apri in Google Maps
                        </a>
                        <button
                          data-testid={`studio-select-${s.id}`}
                          onClick={(e) => { e.stopPropagation(); setStudioId(s.id); setStep(3); }}
                          className="text-xs font-semibold text-white bg-[#2D4A3E] hover:bg-[#1A2F26] transition-colors px-3 py-1.5 rounded-lg"
                        >
                          Scegli questo studio
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-between">
              <Button variant="ghost" data-testid="step2-back" onClick={() => setStep(1)} className="h-12 text-[#5C7066]">
                <ArrowLeft size={16} className="mr-1" /> Indietro
              </Button>
              <Button
                data-testid="step2-next"
                disabled={!canNext2}
                onClick={() => setStep(3)}
                className="h-14 px-6 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26] text-white"
              >
                Continua <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="fade-up">
            <div className="overline mb-2">Passaggio 3 di 4</div>
            <h1 className="font-display text-3xl text-[#1A231F]">Scegli giorno e ora</h1>
            <p className="text-[#5C7066] mt-2">
              {currentStudio?.name} · Durata 45 min
            </p>

            <div className="mt-4">
              <Button
                data-testid="first-available-btn"
                variant="outline"
                onClick={findFirstAvailable}
                className="h-12 rounded-xl border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white"
              >
                <Sparkles size={16} className="mr-2" />
                Trova il primo appuntamento disponibile
              </Button>
            </div>

            <div className="mt-6">
              {loadingSlots && (
                <div data-testid="slots-loading" className="flex flex-col items-center justify-center gap-3 py-14">
                  <Loader2 size={40} className="text-[#2D4A3E] animate-spin" strokeWidth={1.75} />
                  <div className="text-sm text-[#5C7066]">Ricerca degli orari disponibili…</div>
                </div>
              )}
              {!loadingSlots && sortedDates.length === 0 && (
                <div className="card-soft p-6 text-sm text-[#5C7066]" data-testid="no-slots">
                  Nessuna disponibilità nei prossimi 30 giorni. Contattami su WhatsApp.
                </div>
              )}
              <div className="space-y-5">
                {sortedDates.map((d) => (
                  <div key={d} data-testid={`slot-day-${d}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarIcon size={14} className="text-[#2D4A3E]" />
                      <div className="text-sm font-medium text-[#1A231F]">{fmtItalianDate(d + "T12:00:00Z")}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {slotsByDate[d].map((s) => {
                        const active = selectedSlot?.start === s.start;
                        return (
                          <button
                            key={s.start}
                            data-testid={`slot-${s.start}`}
                            onClick={() => setSelectedSlot({ date: d, ...s })}
                            className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                              active
                                ? "bg-[#2D4A3E] text-white border-[#2D4A3E]"
                                : "bg-white text-[#1A231F] border-[#E0E5E2] hover:border-[#8AA399]"
                            }`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <Button variant="ghost" data-testid="step3-back" onClick={() => setStep(studioPreSelected ? 1 : 2)} className="h-12 text-[#5C7066]">
                <ArrowLeft size={16} className="mr-1" /> Indietro
              </Button>
              <Button
                data-testid="step3-next"
                disabled={!canNext3}
                onClick={() => setStep(4)}
                className="h-14 px-6 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26] text-white"
              >
                Continua <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="fade-up">
            <div className="overline mb-2">Passaggio 4 di 4</div>
            <h1 className="font-display text-3xl text-[#1A231F]">I tuoi dati</h1>
            <div className="card-soft p-4 mt-4 text-sm text-[#5C7066]">
              <div className="flex items-center gap-2"><Clock size={14} /> {selectedSlot && `${formatDay(selectedSlot.date)} alle ${selectedSlot.label}`}</div>
              <div className="flex items-center gap-2 mt-1"><MapPin size={14}/> {currentStudio?.name}</div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div>
                <Label htmlFor="fn">Nome <span className="text-[#B85C5C]">*</span></Label>
                <Input id="fn" data-testid="input-firstname" className="h-12 mt-1 rounded-xl bg-white" value={form.first_name} onChange={(e)=>setForm({...form, first_name: e.target.value})} />
              </div>
              <div>
                <Label htmlFor="ln">Cognome <span className="text-[#B85C5C]">*</span></Label>
                <Input id="ln" data-testid="input-lastname" className="h-12 mt-1 rounded-xl bg-white" value={form.last_name} onChange={(e)=>setForm({...form, last_name: e.target.value})} />
              </div>
              <div>
                <Label htmlFor="ph">Telefono <span className="text-[#B85C5C]">*</span></Label>
                <Input id="ph" data-testid="input-phone" type="tel" className="h-12 mt-1 rounded-xl bg-white" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} />
              </div>
              <div>
                <Label htmlFor="em">Email <span className="text-[#B85C5C]">*</span></Label>
                <Input id="em" data-testid="input-email" type="email" className="h-12 mt-1 rounded-xl bg-white" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} />
                <p className="text-xs text-[#5C7066] mt-1">Necessaria per ricevere la conferma e i promemoria dell'appuntamento.</p>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="nt">Breve descrizione del problema</Label>
                <Textarea id="nt" data-testid="input-note" rows={4} className="mt-1 rounded-xl bg-white" value={form.note} onChange={(e)=>setForm({...form, note: e.target.value})} />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3 text-sm text-[#1A231F]">
                <Checkbox data-testid="consent-gdpr" checked={form.consent_gdpr} onCheckedChange={(v)=>setForm({...form, consent_gdpr: !!v})} className="mt-0.5" />
                <span>Acconsento al trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR) per la sola finalità di gestire la prenotazione.</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-[#1A231F]">
                <Checkbox data-testid="consent-privacy" checked={form.consent_privacy} onCheckedChange={(v)=>setForm({...form, consent_privacy: !!v})} className="mt-0.5" />
                <span>Dichiaro di aver preso visione dell&apos;informativa privacy.</span>
              </label>
            </div>

            <div className="mt-8 flex justify-between">
              <Button variant="ghost" data-testid="step4-back" onClick={() => setStep(3)} className="h-12 text-[#5C7066]">
                <ArrowLeft size={16} className="mr-1" /> Indietro
              </Button>
              <Button
                data-testid="submit-booking"
                disabled={!canSubmit || submitting}
                onClick={submit}
                className="h-14 px-7 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26] text-white"
              >
                {submitting ? "Invio…" : (<><Check size={18} className="mr-2"/> Conferma prenotazione</>)}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-10 text-center">
          {!embed && (
            <Link to="/" data-testid="back-home" className="text-xs text-[#5C7066] hover:text-[#2D4A3E]">Torna alla home</Link>
          )}
        </div>
      </main>
    </div>
  );
}
